import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { COLLECTION_ADMINS, COLLECTION_COORDINATORS, COLLECTION_DOCTORS } from '../shared/constants';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [userName, setUserName] = useState(null); // Firestore display name
    const [loading, setLoading] = useState(true);

    const getUserInfo = React.useCallback(async (uid) => {
        try {
            // Check admins collection
            const adminDoc = await getDoc(doc(db, COLLECTION_ADMINS, uid));
            if (adminDoc.exists()) {
                const data = adminDoc.data();
                return { role: 'admin', name: data.fullName || data.name || data.email || null };
            }

            // Check coordinators collection
            const coordinatorDoc = await getDoc(doc(db, COLLECTION_COORDINATORS, uid));
            if (coordinatorDoc.exists()) {
                const data = coordinatorDoc.data();
                return { role: 'coordinator', name: data.fullName || data.name || data.email || null };
            }

            // Check doctors collection
            const doctorDoc = await getDoc(doc(db, COLLECTION_DOCTORS, uid));
            if (doctorDoc.exists()) {
                return { role: 'doctor', name: null };
            }

            return { role: null, name: null };
        } catch (error) {
            console.error('Error getting user info:', error);
            return { role: null, name: null };
        }
    }, []);

    const signIn = React.useCallback(async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const uid = userCredential.user.uid;

            // Get user role and name from Firestore
            const { role, name } = await getUserInfo(uid);

            if (!role) {
                await firebaseSignOut(auth);
                throw new Error('Unauthorized. No user record found.');
            }

            // Only admins and coordinators can use the web dashboard
            if (role !== 'admin' && role !== 'coordinator') {
                await firebaseSignOut(auth);
                throw new Error('This dashboard is for admins and coordinators only.');
            }

            setUserRole(role);
            setUserName(name);
            return { success: true, role };
        } catch (error) {
            console.error('Sign in error:', error);
            throw error;
        }
    }, [getUserInfo]);

    const signOut = React.useCallback(async () => {
        await firebaseSignOut(auth);
        setUserRole(null);
        setUserName(null);
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);

            if (user) {
                const { role, name } = await getUserInfo(user.uid);
                setUserRole(role);
                setUserName(name);
            } else {
                setUserRole(null);
                setUserName(null);
            }

            setLoading(false);
        });

        return unsubscribe;
    }, [getUserInfo]);

    const value = useMemo(() => ({
        currentUser,
        userRole,
        userName,
        signIn,
        signOut,
    }), [currentUser, userRole, userName, signIn, signOut]);

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
